import React, { ChangeEvent, FC, useContext, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  SelectChangeEvent,
  Snackbar,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Check, Delete, Edit } from '@mui/icons-material';
import GMBService from '../../../services/GMBService';
import { IGMBComment } from '../IGMBComment';
import { useTranslation } from 'react-i18next';
import CommentAiService, {
  IGenerateCommentOptions,
  IReviewMetadata,
} from '../../../services/CommentAiService';
import { CommunityManagement } from '../../../shared/interfaces/ICommunityManagement';
import { AuthContext } from '../../../providers/AuthProvider';
import { constants } from '../../../constants/constants';
import LanguageControl from '../../Language/LanguageControl';
import CommentStatusSelector from '../../CommentStatusSelector/CommentStatusSelector';
import { IGMBReviewComment } from '../../../shared/interfaces/IGMBReviewEntry';
import CommentingService, { CommentStatus } from '../../../services/CommentingService';

interface Props {
  id: string;
  reviewerName: string;
  commentStatus: CommentStatus;
  reviewId: string;
  locationId: string;
  comment: string;
  input: string;
  noReviewText: boolean;
  reviewReply: IGMBComment | null;
  communityManagementSettings: CommunityManagement;
  reviewMetadata: IReviewMetadata;
  reviewComment: IGMBReviewComment;
  onReviewCommentChange: (reviewEntryId: string, updates: Partial<IGMBReviewComment>) => void;
}

const CommentContainer = styled.div<{ reviewreply: IGMBComment | null }>`
  display: flex;
  flex-direction: column;
  background-color: ${(props) =>
    props.reviewreply !== null
      ? props.theme.palette.background.default
      : props.theme.palette.background.paper};
  padding: ${(props) => (props.reviewreply !== null ? props.theme.spacing(2) : '0')};
  border-radius: ${(props) => props.theme.shape.borderRadius}px;
`;

const GMBReviewReply: FC<Props> = ({
  id,
  reviewerName,
  comment,
  reviewReply,
  reviewId,
  locationId,
  communityManagementSettings,
  input,
  noReviewText,
  reviewMetadata,
  reviewComment,
  onReviewCommentChange,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { hasRole } = useContext(AuthContext);
  const [commentStatus, setCommentStatus] = useState<CommentStatus>(reviewComment.status);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [reply, setReply] = useState<IGMBComment | null>(reviewReply);
  const [replied, setReplied] = useState<boolean>(false);
  const [deleted, setDeleted] = useState<boolean>(false);
  const [editing, setEditing] = useState<boolean>(false);
  const commentRef = useRef<HTMLInputElement | null>(null);
  const [language, setLanguage] = useState<Language | ''>(
    communityManagementSettings.languagePreference
  );

  const handlePostReply = async () => {
    const newComment = reply?.comment ?? reviewComment?.text;
    if (!newComment) return;
    const result = await GMBService.replyToReview(locationId, reviewId, newComment, setLoading);
    if (!result) {
      setErrorMessage(t('reply_failed'));
      return;
    }
    await CommentingService.saveReviewComment(id, 'DONE', newComment, () => {});
    setCommentStatus('DONE');
    onReviewCommentChange(id, { text: newComment, status: 'DONE' });
    setReplied(true);
    setEditing(false);
  };

  const setReplyAndSave = (newComment: string) => {
    CommentingService.saveReviewComment(id, commentStatus, newComment, () => {});
    onReviewCommentChange(id, { text: newComment, status: commentStatus });
    setReply((prevState) => ({ ...prevState, comment: newComment }));
  };

  const handleDeleteReply = async () => {
    const result = await GMBService.deleteReviewReply(locationId, reviewId, setLoading);
    if (!result) {
      setErrorMessage(t('reply_delete_failed'));
      return;
    }
    setDeleted(true);
    setEditing(false);
  };

  const handleGenerateReply = async () => {
    const generateCommentOptions: IGenerateCommentOptions = {
      language: language,
      tonality: constants.tonality[communityManagementSettings.tonality.preference],
      duzen: communityManagementSettings.pronoun.preference === 0,
      review: input,
      useCustomerName: communityManagementSettings.useUsername,
      salutation: communityManagementSettings.salutationPrefix,
      signature: communityManagementSettings.greeting,
      additionalPrompt: communityManagementSettings.additionalPrompt,
      reviewMetadata: reviewMetadata,
      noReviewText: noReviewText,
    };
    const data = await CommentAiService.generateComment(generateCommentOptions, setLoading);

    if (!data) return;
    CommentingService.saveReviewComment(id, commentStatus, data, () => {});
    onReviewCommentChange(id, { text: data, status: commentStatus });
    setReply({
      comment: data,
    });
  };

  let statusBox = null;
  if (hasRole('can-use-ai')) {
    statusBox = (
      <CommentStatusSelector
        disabled={loading}
        value={commentStatus}
        onChange={async (event: SelectChangeEvent) => {
          setLoading(true);
          setCommentStatus(event.target.value as CommentStatus);
          await CommentingService.saveReviewComment(
            id,
            event.target.value,
            reply?.comment ?? reviewComment.text,
            setLoading
          );
          onReviewCommentChange(id, {
            status: event.target.value as CommentStatus,
          });
        }}
      />
    );
  }

  return (
    <>
      {reviewReply !== null ? (
        <CommentContainer reviewreply={reviewReply}>
          {!deleted ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
              <Box sx={{ flex: 1, whiteSpace: 'pre-line' }}>
                {editing ? (
                  <TextField
                    label={t('comment')}
                    inputRef={commentRef}
                    multiline
                    rows={8}
                    fullWidth
                    InputLabelProps={{ shrink: !!(reply?.comment || reviewComment?.text) }}
                    value={reply?.comment || reviewComment?.text}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      setReply((prevState) => ({ ...prevState, comment: event.target.value }))
                    }
                  />
                ) : (
                  <Typography variant="body2">{reviewReply.comment}</Typography>
                )}
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'stretch',
                  my: theme.spacing(2),
                }}
              >
                {editing ? (
                  <Box
                    sx={{
                      display: 'flex',
                    }}
                  >
                    {hasRole('can-use-ai') && (
                      <>
                        <LanguageControl
                          language={language}
                          setLanguage={setLanguage}
                        />
                        <Button
                          disabled={loading}
                          variant="contained"
                          onClick={async () => await handleGenerateReply()}
                          sx={{ mr: theme.spacing(1) }}
                        >
                          {t('generate')}
                        </Button>
                      </>
                    )}
                    <Button
                      disabled={loading || !reply!.comment}
                      variant="contained"
                      onClick={async () => await handlePostReply()}
                      sx={{ ml: 1 }}
                    >
                      {t('to_comment')}
                    </Button>
                    <Button
                      disabled={loading}
                      variant="contained"
                      color="warning"
                      onClick={() => {
                        setReply({ comment: '' });
                      }}
                      sx={{ ml: 1 }}
                    >
                      {t('reset')}
                    </Button>
                    {loading && (
                      <CircularProgress
                        sx={{ ml: theme.spacing(1) }}
                        size={24}
                      />
                    )}
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                    {loading && (
                      <CircularProgress
                        sx={{ ml: theme.spacing(1) }}
                        size={24}
                      />
                    )}
                    <IconButton
                      disabled={loading}
                      onClick={async () => {
                        if (editing) {
                          setEditing(false);
                        } else {
                          setEditing(true);
                        }
                      }}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      disabled={loading}
                      color="error"
                      onClick={async () => await handleDeleteReply()}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                )}
                {statusBox}
              </Box>
              {hasRole('can-use-ai') && (
                <Typography
                  sx={{ marginLeft: '0.5rem', marginTop: '0.2rem' }}
                  variant="caption"
                >
                  {t('ai_disclaimer_reply')}
                </Typography>
              )}
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'center', p: theme.spacing(4) }}>
                <Check color="primary" />
                <Typography
                  variant="body1"
                  sx={{ ml: theme.spacing(1) }}
                >
                  {t('reply_deleted')}
                </Typography>
              </Box>
              {statusBox}
            </>
          )}
        </CommentContainer>
      ) : (
        <CommentContainer reviewreply={reviewReply}>
          {!replied ? (
            <>
              <TextField
                label={t('comment')}
                inputRef={commentRef}
                multiline
                rows={8}
                InputLabelProps={{ shrink: !!(reply?.comment || reviewComment?.text) }}
                value={reply?.comment || reviewComment?.text}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setReplyAndSave(event.target.value)
                }
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {hasRole('can-use-ai') && (
                    <>
                      <LanguageControl
                        language={language}
                        setLanguage={setLanguage}
                      />
                      <Button
                        disabled={loading}
                        variant="contained"
                        onClick={async () => await handleGenerateReply()}
                        sx={{ mr: theme.spacing(1) }}
                      >
                        {t('generate')}
                      </Button>
                    </>
                  )}
                  <Button
                    disabled={loading}
                    variant="contained"
                    onClick={async () => await handlePostReply()}
                  >
                    {t('to_comment')}
                  </Button>
                  <Button
                    disabled={loading}
                    variant="contained"
                    color="warning"
                    onClick={() => {
                      if (commentRef.current) {
                        commentRef.current.value = '';
                      }
                      setReply(null);
                    }}
                    sx={{ ml: 1 }}
                  >
                    {t('reset')}
                  </Button>
                  {loading && (
                    <CircularProgress
                      sx={{ ml: theme.spacing(1) }}
                      size={24}
                    />
                  )}
                </Box>
                {statusBox}
              </Box>
              {hasRole('can-use-ai') && (
                <Typography
                  sx={{ marginLeft: '0.5rem', marginTop: '0.2rem' }}
                  variant="caption"
                >
                  {t('ai_disclaimer_reply')}
                </Typography>
              )}
            </>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: theme.spacing(4) }}>
              <Check color="primary" />
              <Typography
                variant="body1"
                sx={{ ml: theme.spacing(1) }}
              >
                {t('reply_published')}
              </Typography>
              {statusBox}
            </Box>
          )}
        </CommentContainer>
      )}
      <Snackbar
        open={errorMessage !== null}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setErrorMessage(null)}
          severity="error"
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default GMBReviewReply;
